// ── Slim Navbar (top header bar) ──

import { escapeHtml } from '../utils.js';
import { logout } from '../auth.js';
import { openMobileSidebar, closeMobileSidebar } from './sidebar.js';
import { renderThemeToggle, bindThemeToggle } from '../ui.js';
import { bindInstallButton } from '../pwa-install.js';
import { openNotificationsModal } from './notifications-modal.js';
import { subscribeUnread, refreshUnreadCount, startUnreadPolling, stopUnreadPolling } from '../lib/notifications.js';

const HAMBURGER_ICON = `<svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>`;

const BELL_ICON = `<svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>`;

const CHEVRON_DOWN = `<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" fill="none" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>`;

const DOWNLOAD_ICON = `<svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12"/><polyline points="7 10 12 15 17 10"/><path d="M4 19h16"/></svg>`;

const SHIELD_ICON = `<svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>`;

const CLOSE_ICON = `<svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`;

export const TWO_FA_BANNER_DISMISSED_KEY = 'tt_2fa_banner_dismissed';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
}

// Header bell control (moved off the sidebar/dashboard in TPT27) — opens the shared
// notifications modal (components/notifications-modal.js) and carries the unread-count
// badge sourced from lib/notifications.js. Present on every page since the navbar is part
// of the persistent app shell (layout.js), unlike the old dashboard-only button.
function renderNotificationsButton() {
  return `<button class="navbar-notif-btn" id="navbar-notif-btn" type="button"
    title="Notifications" aria-label="Notifications">
    ${BELL_ICON}
    <span class="navbar-notif-badge" id="navbar-notif-badge" hidden></span>
  </button>`;
}

// Builds the .navbar-avatar node — either the uploaded/Google <img> or the
// initials fallback <div>. Shared by renderNavbar() (first paint) and
// refreshNavbarUser() (TPT81, after a profile save) so both paths apply the
// same avatar_src/avatar_url precedence and the same data-initials fallback
// hook.
//
// `bust`: only set by refreshNavbarUser(). An uploaded avatar's avatar_src is
// the CONSTANT string /api/auth/me/avatar (api/src/lib/avatar.js's
// ME_AVATAR_PATH) — replacing the photo does not change the URL — and that
// route serves `Cache-Control: private, no-cache` with no ETag/Last-Modified,
// so a same-URL swap is exactly where a browser is entitled to reuse the old
// bytes. A throwaway `?v=` query param forces a real refetch; the route
// ignores unknown params. First paint (renderNavbar) never needs this.
function renderNavbarAvatar(user, { bust = false } = {}) {
  const u = user || {};
  const initials = getInitials(u.name);
  // (TPT76) avatar_src (server-resolved precedence, api/src/lib/avatar.js) wins
  // over avatar_url — falls back to avatar_url for any older/synthetic user shape.
  let src = u.avatar_src || u.avatar_url;
  if (src && bust && src.startsWith('/api/')) {
    src += (src.includes('?') ? '&' : '?') + 'v=' + Date.now();
  }
  // No inline onerror here — helmet's default script-src-attr 'none' (api/src/index.js)
  // blocks inline event handlers, so a broken-image icon would never fall back to
  // initials. bindNavbarAvatarFallback() (mountNavbar()) binds the real handler instead.
  return src
    ? `<img id="navbar-avatar-img" src="${escapeHtml(src)}" alt="" class="navbar-avatar" referrerpolicy="no-referrer" data-initials="${escapeHtml(initials)}">`
    : `<div class="navbar-avatar navbar-avatar-initials">${escapeHtml(initials)}</div>`;
}

export function renderNavbar(user) {
  const u = user || {};

  return `<header class="navbar" id="navbar">
    <div class="navbar-left">
      <button class="navbar-hamburger" id="navbar-hamburger" aria-label="Toggle menu">${HAMBURGER_ICON}</button>
      <div class="navbar-breadcrumb" id="navbar-breadcrumb"></div>
    </div>
    <div class="navbar-right">
      <button class="btn btn-ghost btn-sm pwa-install-btn" id="pwa-install" hidden title="Install app" aria-label="Install app">${DOWNLOAD_ICON}<span>Install app</span></button>
      ${renderThemeToggle()}
      ${renderNotificationsButton()}
      <div class="navbar-user" id="navbar-user">
        ${renderNavbarAvatar(u)}
        <span class="navbar-user-name">${escapeHtml(u.name || '')}</span>
        ${CHEVRON_DOWN}
      </div>
      <div class="navbar-user-menu" id="navbar-user-menu">
        <a href="#/profile" class="navbar-menu-item" id="navbar-profile-link">Profile</a>
        <a class="navbar-menu-item" id="navbar-settings-link" hidden>Settings</a>
        <a href="#/account/security" class="navbar-menu-item">Security</a>
        <button class="navbar-menu-item" id="navbar-logout">Logout</button>
      </div>
    </div>
  </header>`;
}

// TPT81 — mountNavbar() runs exactly once per signed-in session
// (initLayout() is gated on isLayoutMounted()), so a profile save's new
// name/avatar would otherwise sit uselessly in the auth:user-updated event
// with no listener and the chip would stay stale until a hard reload. Patches
// the two nodes in place rather than re-rendering the whole navbar, which
// would drop the notifications subscription and the theme/install bindings.
//
// The avatar node is REPLACED (outerHTML), not just given a new `src`: a save
// can flip between the uploaded/Google <img> and the initials <div>
// (renderNavbarAvatar's two branches), and the old <img>'s error listener
// (bindNavbarAvatarFallback) dies with the node it was bound to, so it's
// re-bound after the swap.
export function refreshNavbarUser(user) {
  const host = document.getElementById('navbar-user');
  if (!host) return;
  const nameEl = host.querySelector('.navbar-user-name');
  if (nameEl) nameEl.textContent = (user && user.name) || '';
  const current = host.querySelector('.navbar-avatar');
  if (!current) return;
  current.outerHTML = renderNavbarAvatar(user, { bust: true });
  bindNavbarAvatarFallback();
}

// Same red-pill/99+/hidden-at-zero rules the old sidebar and dashboard badges used, now
// driven by one navbar subscriber (see mountNavbar()). The avatar dropdown trigger carried
// its own copy of this badge until TPT60 removed it as redundant with the bell.
function updateNotificationBadge(count) {
  const badge = document.getElementById('navbar-notif-badge');
  if (!badge) return;
  badge.textContent = count > 99 ? '99+' : String(count);
  badge.hidden = !count;
}

export function renderTwoFaBanner(user) {
  if (!user || user.has_password !== true || user.totp_enabled !== false) return '';

  return `<div class="twofa-banner" id="twofa-banner" role="region" aria-label="Two-factor authentication recommendation">
    <div class="twofa-banner-icon">${SHIELD_ICON}</div>
    <div class="twofa-banner-copy">
      <strong>Protect your account with 2FA</strong>
      <span>Recommended for password sign-in. Google sign-in already stays separate and does not require this.</span>
    </div>
    <a class="btn btn-primary btn-sm twofa-banner-link" href="#/account/security">Enable 2FA</a>
    <button class="twofa-banner-dismiss" id="twofa-banner-dismiss" type="button" aria-label="Dismiss 2FA recommendation" title="Dismiss">${CLOSE_ICON}</button>
  </div>`;
}

let docClickHandler = null;
let unsubscribeUnread = null;
let userUpdatedHandler = null;

// (TPT76) Replaces the old inline onerror="…" (blocked by helmet's
// script-src-attr: 'none', so it never actually ran) — a broken/unloadable
// avatar image swaps to the initials div instead of staying a broken-image icon.
// Also checks the already-failed case at bind time: the <img> starts loading
// during initLayout()'s innerHTML parse, before mountNavbar() runs, so a fast
// failure can land before this listener attaches.
function bindNavbarAvatarFallback() {
  const img = document.getElementById('navbar-avatar-img');
  if (!img) return;
  const fallback = () => {
    const div = document.createElement('div');
    div.className = 'navbar-avatar navbar-avatar-initials';
    div.textContent = img.dataset.initials || '?';
    img.replaceWith(div);
  };
  if (img.complete && img.naturalWidth === 0) {
    fallback();
    return;
  }
  img.addEventListener('error', fallback);
}

export function mountNavbar() {
  bindNavbarAvatarFallback();

  // Hamburger toggle
  const hamburger = document.getElementById('navbar-hamburger');
  if (hamburger) {
    hamburger.addEventListener('click', () => {
      const sidebar = document.getElementById('sidebar');
      if (sidebar && sidebar.classList.contains('mobile-open')) {
        closeMobileSidebar();
      } else {
        openMobileSidebar();
      }
    });
  }

  // Sidebar overlay close
  const overlay = document.getElementById('sidebar-overlay');
  if (overlay) {
    overlay.addEventListener('click', closeMobileSidebar);
  }

  // User menu dropdown
  const userBtn = document.getElementById('navbar-user');
  const userMenu = document.getElementById('navbar-user-menu');
  if (userBtn && userMenu) {
    userBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      userMenu.classList.toggle('open');
    });
  }

  // Account-level Profile is available in every context. Close immediately on
  // selection rather than waiting for the document click so route resolution
  // never paints the next page with the persistent dropdown still open.
  const profileLink = document.getElementById('navbar-profile-link');
  if (profileLink && userMenu) {
    profileLink.addEventListener('click', () => userMenu.classList.remove('open'));
  }

  // Theme toggle
  bindThemeToggle();

  // PWA install button
  bindInstallButton();

  // Notifications bell — opens the shared modal; stopPropagation keeps this self-contained
  // from the user-menu's own outside-click handler below (same idiom as that handler's own
  // stopPropagation on #navbar-user).
  const notifBtn = document.getElementById('navbar-notif-btn');
  if (notifBtn) {
    notifBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      openNotificationsModal();
    });
  }

  // Unread-count badge (bell) + live poll/OS push (moved off sidebar.js in TPT27).
  // mountNavbar() runs exactly once per signed-in session — initLayout() in layout.js is
  // gated on isLayoutMounted() — so this can't double-subscribe or start two polls.
  // destroyNavbar() unsubscribes and stops the poll.
  unsubscribeUnread = subscribeUnread(updateNotificationBadge);
  refreshUnreadCount();
  startUnreadPolling();

  // Logout
  const logoutBtn = document.getElementById('navbar-logout');
  if (logoutBtn) {
    logoutBtn.addEventListener('click', logout);
  }

  // Close menu on outside click
  docClickHandler = () => {
    if (userMenu) userMenu.classList.remove('open');
  };
  document.addEventListener('click', docClickHandler);

  // TPT81 — repaint the chip after a profile save (see refreshNavbarUser()).
  userUpdatedHandler = (e) => refreshNavbarUser(e.detail && e.detail.user);
  window.addEventListener('auth:user-updated', userUpdatedHandler);
}

// Settings is project-scoped: there is no project-less #/settings route. Every
// page render pushes its current project context through updateLayoutContext()
// in layout.js, keeping this href aligned across project switches and hiding the
// item on Dashboard, All Tasks, Profile, and Security.
export function updateNavbarSettingsLink(projectId) {
  const link = document.getElementById('navbar-settings-link');
  if (!link) return;
  const hasProject = projectId !== null && projectId !== undefined && projectId !== '';
  if (hasProject) {
    link.href = '#/projects/' + projectId + '/settings';
    link.hidden = false;
  } else {
    link.hidden = true;
    link.removeAttribute('href');
  }
}

export function updateNavbarBreadcrumb(projectId, projectName, viewName) {
  const bc = document.getElementById('navbar-breadcrumb');
  if (!bc) return;

  if (projectName && projectId) {
    bc.innerHTML = `<a href="#/projects/${projectId}">${escapeHtml(projectName)}</a><span class="navbar-breadcrumb-sep">/</span>${escapeHtml(viewName || '')}`;
  } else {
    bc.textContent = viewName || '';
  }
}

export function destroyNavbar() {
  if (docClickHandler) {
    document.removeEventListener('click', docClickHandler);
    docClickHandler = null;
  }
  if (userUpdatedHandler) {
    window.removeEventListener('auth:user-updated', userUpdatedHandler);
    userUpdatedHandler = null;
  }
  if (unsubscribeUnread) {
    unsubscribeUnread();
    unsubscribeUnread = null;
  }
  stopUnreadPolling();
}
